import { __ } from '@wordpress/i18n';
import { registerPaymentMethod } from '@woocommerce/blocks-registry';
import { decodeEntities } from '@wordpress/html-entities';
import { useEffect, useState, useCallback, useRef } from '@wordpress/element';
import { getPaymentMethodData } from '@woocommerce/settings';
import { ValidatedTextInput } from '@woocommerce/blocks-checkout';

import creditCardType, {
  getTypeInfo,
  types as CardType,
} from "credit-card-type";


const { useSelect } = window.wp.data;
const { CHECKOUT_STORE_KEY } = window.wc.wcBlocksData;
const { PAYMENT_STORE_KEY } = window.wc.wcBlocksData;

const settings = getPaymentMethodData( 'valitorpay', {} );
const defaultLabel = __( 'Valitorpay', 'valitorpay-payment-gateway-for-woocommerce' );
const label = decodeEntities( settings?.title || '' ) || defaultLabel;

const ValitorPayDesc = () => {
	return decodeEntities( settings.description || '' );
};

const ValitorPayTestCardNotice = () => {
	if(settings.testmode){
		const testModeText = __( 'In test mode, you can use cardNumbers: 5204730000000021(MasterCard), 4761340000000118(Visa), 341111000000151(Amex)', 'valitorpay-payment-gateway-for-woocommerce' );

		return <div class="valitorpay-testmode-notice"><i>{testModeText}</i></div>
	}
};

/**
 * Content component
 */
const Content = (props) => {
	const [cardValue,setCardValue] = useState('');
	const [cardExpiryValue, setCardExpiryValue] = useState('');
	const [cardCodeValue,setCardCodeValue] = useState('');

	const { eventRegistration, emitResponse } = props;
	const { onPaymentSetup } = eventRegistration;
	useEffect( () => {
		const unsubscribe = onPaymentSetup( async () => {
			const cardData = {};
			if ( cardValue ) {
				cardData['valitorpay-card-number'] = cardValue;
			}
			if ( cardExpiryValue ) {
				cardData['valitorpay-card-expiry'] = cardExpiryValue;
			}
			if ( cardCodeValue ) {
				cardData['valitorpay-card-cvc'] = cardCodeValue;
			}

			if(Object.keys(cardData).length !== 0){
				return {
					type: emitResponse.responseTypes.SUCCESS,
					meta: {
						paymentMethodData: cardData
					},
				};
			}

			return {
				type: emitResponse.responseTypes.ERROR,
				message: 'There was an error',
			};
		} );
		// Unsubscribes when this component is unmounted.
		return () => {
			unsubscribe();
		};
	}, [
		emitResponse.responseTypes.ERROR,
		emitResponse.responseTypes.SUCCESS,
		onPaymentSetup,
		cardValue,
		cardExpiryValue,
		cardCodeValue
	] );


	return [
		<ValitorPayDesc />,
		<ValitorPayTestCardNotice />,
		<div className={ 'valitorpay-fields wc-block-card-elements' }>
			<div className={ 'valitorpay-payment-field valitorpay-payment-card wc-block-gateway-container wc-card-number-element' }>
				<ValidatedTextInput
					id="valitorpay-card-number"
					name="valitorpay-card-number"
					type="tel"
					required={true}
					class={'valitorpay-input valitorpay-card-input'}
					label={
							__( 'Card number', 'valitorpay-payment-gateway-for-woocommerce' )
					}
					inputmode="numeric"
					maxlength="19"
					autocomplete="cc-number"
					value={cardValue}
					onChange={(nextValue)=>{
						if(!nextValue){
							setCardValue('');
						}else if( /^-?\d+$/.test(nextValue) ){
							setCardValue(nextValue);
						}else{
							setCardValue(cardValue ??'');
						}
					}}
					customValidation={ ( inputObject,cardClass ) => {
						if(inputObject.value && parseInt(inputObject.value.length)>4){
							const cardType = creditCardType(inputObject.value);
							if(cardType.length == 1){
								const lengths = cardType[0]['lengths'];
								if(parseInt(lengths[0])<=parseInt(inputObject.value.length)){
									return true;
								}
							}
						}

						inputObject.setCustomValidity(
							__( 'Please enter a valid Card number', 'valitorpay-payment-gateway-for-woocommerce' )
						);
						return false;
					} }
				/>
			</div>
			<div className={ 'valitorpay-payment-field valitorpay-payment-field-c-expiry wc-block-gateway-container wc-card-expiry-element' }>
				<ValidatedTextInput
					id="valitorpay-card-expiry"
					type="tel"
					required={true}
					class={'valitorpay-input valitorpay-card-expiry-input'}
					label={
						__( 'Expiry (MM/YY)', 'valitorpay-payment-gateway-for-woocommerce' )
					}
					value={cardExpiryValue}
					maxlength="5"
					onChange={(nextValue)=>{
						const parts = nextValue.split('/');
						const length = nextValue.length;
						if(cardExpiryValue.length>length){
							if(length == 2){
								nextValue = nextValue.slice(0, -1);
							}
							setCardExpiryValue(nextValue ??'');
						}else{
							let valid = true;
							parts.forEach((value, index) => {
								if(/^-?\d+$/.test(value)){
									if(index===0){
										if(value<=12){
											if(value==='00'){
												valid = false;
											}else{
												valid = true;
											}
										}else{
											valid = false;
										}
									}else{
										valid = true;
									}
								}else{
									valid = false;
								}
							});
							if(length == 2){
								nextValue = nextValue + '/';
							}
							if(valid){
								setCardExpiryValue(nextValue ??'');
							}else{
								setCardExpiryValue(cardExpiryValue ??'');
							}
						}
					}}
					customValidation={ ( inputObject ) => {
						if(inputObject.value && parseInt(inputObject.value.length)>4){
							const parts = inputObject.value.split('/');
							const date = new Date ();
							const month = date.getMonth();
							const year = date.getFullYear();
							const inputYear = parseInt("20"+ parts[1]);
							const inputMonth = parseInt(parts[0]);
							if(inputYear>year && inputYear<(year+5)){
								if(inputMonth>0 && inputMonth<=12){
									return true;
								}
							}else if(inputYear==year){
								if(inputMonth>=(month+1) && inputMonth<=12){
									return true;
								}
							}
						}

						inputObject.setCustomValidity(
							__( 'Please enter a valid Expiry (MM/YY)', 'valitorpay-payment-gateway-for-woocommerce' )
						);
						return false;
					}}
				/>
			</div>
			<div className={ 'valitorpay-payment-field valitorpay-payment-field-cvc wc-block-gateway-container wc-card-cvc-element' }>
				<ValidatedTextInput
					id="valitorpay-card-cvc"
					type="tel"
					name="valitorpay-card-cvc"
					required={true}
					class={'valitorpay-input valitorpay-card-cvc-input'}
					autocorrect="no"
					spellcheck="no"
					label={
						__( 'Card code', 'valitorpay-payment-gateway-for-woocommerce' )
					}
					maxlength="4"
					value={cardCodeValue}
					onChange={(nextValue)=>{
						if(/^-?\d+$/.test(nextValue)){
							setCardCodeValue(nextValue ??'');
						}else{
							setCardCodeValue(cardCodeValue ??'');
						}
					}}
				/>
			</div>
		</div>
	];
};

/**
 * Label component
 *
 * @param {*} props Props from payment API.
 */
const Label = ( props ) => {
	const { PaymentMethodLabel } = props.components;
	return <PaymentMethodLabel text={ label } />;
};

/**
 * Payment method config object.
 */
const ValitorpayPaymentMethod = {
	name: 'valitorpay',
	label: <Label />,
	content: <Content />,
	edit: <Content />,
	canMakePayment: () => true,
	ariaLabel: label,
	supports: {
		features: settings?.supports ?? [],
	},
};

registerPaymentMethod( ValitorpayPaymentMethod );